import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { tKey } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import {
  type BenchmarkInsightCompetitorsCompetitor,
  BenchmarkInsightCompetitorsDesiredCompetitor,
} from 'lumen/models';
import {
  PRIORITIZED_CATEGORIES,
  type PrioritizedCategory,
} from 'lumen/models/benchmark-insight/competitors/proposed-compset';

interface HotelBenchmarkInsightCompetitorsTdNameSignature {
  Args: {
    column: {
      compsetPrioritizedCategory: PrioritizedCategory;
      showDesiredCompetitorAvailability: boolean;
    };
    row: {
      isSuggestedCompetitor?: boolean;
    };
    value:
      | BenchmarkInsightCompetitorsCompetitor
      | BenchmarkInsightCompetitorsDesiredCompetitor;
  };
}

const PROPOSED_COMPSET_COMPETITOR_TOOLTIP_TRANSLATIONS: Record<
  PrioritizedCategory,
  string
> = {
  [PRIORITIZED_CATEGORIES.RECOMMENDED]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.competitor_tooltip.recommended',
  ),
  [PRIORITIZED_CATEGORIES.ADR]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.competitor_tooltip.adr',
  ),
  [PRIORITIZED_CATEGORIES.HOTEL_CLASS]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.competitor_tooltip.hotel_class',
  ),
  [PRIORITIZED_CATEGORIES.DISTANCE]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.competitor_tooltip.distance',
  ),
};

export default class HotelBenchmarkInsightCompetitorsTdName extends Component<HotelBenchmarkInsightCompetitorsTdNameSignature> {
  @service declare intl: Services['intl'];

  get showCompsetAvailableIcon(): boolean {
    const { value, column } = this.args;
    return (
      column.showDesiredCompetitorAvailability &&
      value instanceof BenchmarkInsightCompetitorsDesiredCompetitor &&
      value.compsetAvailable
    );
  }

  get tooltipText(): string {
    const { compsetPrioritizedCategory } = this.args.column;
    return this.intl.t(
      PROPOSED_COMPSET_COMPETITOR_TOOLTIP_TRANSLATIONS[
        compsetPrioritizedCategory
      ],
    );
  }

  <template>
    <div class="flex flex-row items-center gap-100">
      {{#if this.showCompsetAvailableIcon}}
        <PrismIcon
          @color="success"
          @name="circle-checkmark"
          data-test-id="desired-competitor-available-icon"
        />
      {{/if}}

      <span
        class="overflow-hidden text-ellipsis whitespace-nowrap"
      >{{@value.competitorName}}</span>

      {{#if @row.isSuggestedCompetitor}}
        <GeneralIconWithTooltip
          @color="intelligence"
          @iconName="artificial-intelligence"
          @size="small"
          @tooltipContent={{this.tooltipText}}
        />
      {{/if}}
    </div>
  </template>
}
